import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useReceiptOcr } from 'hooks/usePayment';
import { paymentStore } from 'store/paymentStore';
import { isGuestOf } from 'common/utils/auth';
import {
    AppScreen, Bleed, Button, ButtonFooter, FieldGroup, PageIntro,
    Section, SectionHeader, TextField,
} from 'components/ds';
import { IcCamera } from 'components/common/icons/EmojiIcons';
import styles from './CostReceipt.module.css';

export const CostReceipt = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { meetingId } = useParams();
    const { status, data: ocrData } = useReceiptOcr();

    const [receiptImage, setReceiptImage] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [extractedData, setExtractedData] = useState<{
        description: string;
        amount: number;
        date: string;
    } | null>(null);

    // 게스트는 영수증 사진 인식을 쓸 수 없다 (CostInput 에서 버튼을 숨김). 주소로 직접 들어오면 돌려보낸다.
    useEffect(() => {
        if (isGuestOf(meetingId)) navigate(-1);
    }, [meetingId, navigate]);

    useEffect(() => {
        if (status === 'ready' && ocrData) {
            setExtractedData({
                description: ocrData.storeName || '지출',
                amount: ocrData.totalAmount || 0,
                date: ocrData.receiptDate ?
                    new Date(ocrData.receiptDate).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).replace(/\. /g, '.').replace(/\.$/, '') :
                    new Date().toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    }).replace(/\. /g, '.').replace(/\.$/, '')
            });
            setIsProcessing(false);
        }
    }, [status, ocrData]);

    useEffect(() => {
        if (status === 'pending') {
            setIsProcessing(true);
        } else if (status === 'error') {
            setIsProcessing(false);
            // 실패가 '0원' 으로 위장되지 않도록 null 로 두고 UI 에서 안내 (extractedData 가 null 이면 미리보기 X)
            setExtractedData(null);
            console.error('[receipt-ocr] OCR failed — paymentStore.receiptOcr error 상태');
        }
    }, [status]);

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && meetingId) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setReceiptImage(reader.result as string);
                setIsProcessing(true);
                // wiz-common 업로드 → wizai 분석 2단계는 store 가 chain 처리
                paymentStore.onReceiptOcr(meetingId, file);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleConfirm = () => {
        // 사용자가 수정한 인식 결과(extractedData)를 CostInput 으로 전달해 form 자동 채움.
        // 이미지 URL 은 OCR 원본(ocrData)에서 보존.
        const ocr = extractedData
            ? {
                storeName: extractedData.description,
                totalAmount: extractedData.amount,
                receiptDate: extractedData.date,
                imageUrl: ocrData?.imageUrl,
            }
            : ocrData;
        navigate(`/cost/${meetingId}`, { state: ocr ? { ocr } : undefined });
    };

    const handleRetake = () => {
        setReceiptImage(null);
        setExtractedData(null);
        setIsProcessing(false);
    };

    return (
        <AppScreen
            title={t('costComp.receiptTitle')}
            bar="close"
            hasFooter={!!(receiptImage && extractedData)}
            onClose={() => navigate(`/cost/${meetingId}`)}
        >
            {!receiptImage ? (
                <>
                    <PageIntro
                        title={t('costComp.addReceipt')}
                        desc={t('costComp.uploadGuideDesc')}
                    />

                    <Section spacing="tight">
                        <SectionHeader title={t('costComp.shootTips')} />
                        <ul className={styles.tips}>
                            <li>{t('costComp.tip1')}</li>
                            <li>{t('costComp.tip2')}</li>
                            <li>{t('costComp.tip3')}</li>
                        </ul>
                    </Section>

                    <Section divided last>
                        <input
                            type="file"
                            id="receipt-upload"
                            className={styles.fileInput}
                            accept="image/*"
                            onChange={handleImageUpload}
                        />
                        <label htmlFor="receipt-upload" className={styles.uploadLabel}>
                            <IcCamera size={18} />
                            {t('costComp.addReceiptBtn')}
                        </label>
                    </Section>
                </>
            ) : (
                <>
                    <Section spacing="tight">
                        <Bleed>
                            <div className={styles.preview}>
                                <img src={receiptImage} alt="" className={styles.previewImg} />
                                {isProcessing && (
                                    <div className={styles.processing}>
                                        <span className={styles.spinner} aria-hidden />
                                        <span>{t('costComp.receiptAnalyzing')}</span>
                                    </div>
                                )}
                            </div>
                        </Bleed>
                    </Section>

                    {extractedData && (
                        <>
                            <Section divided>
                                <SectionHeader title={t('costComp.recognized')} />
                                <FieldGroup>
                                    <TextField
                                        id="ocr-desc"
                                        label={t('costComp.itemLabel')}
                                        type="text"
                                        placeholder={t('costComp.itemPlaceholder')}
                                        value={extractedData.description}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            description: e.target.value,
                                        })}
                                    />
                                    <TextField
                                        id="ocr-amount"
                                        label={t('costComp.amountLabel')}
                                        type="text"
                                        inputMode="numeric"
                                        numeric
                                        value={extractedData.amount.toLocaleString('ko-KR')}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            amount: Number(e.target.value.replace(/[^0-9]/g, '')) || 0,
                                        })}
                                    />
                                    <TextField
                                        id="ocr-date"
                                        label={t('costComp.dateLabel')}
                                        type="text"
                                        placeholder="YYYY.MM.DD"
                                        value={extractedData.date}
                                        onChange={(e) => setExtractedData({
                                            ...extractedData,
                                            date: e.target.value,
                                        })}
                                    />
                                </FieldGroup>
                            </Section>

                            <Section divided last>
                                <p className={styles.hint}>{t('costComp.ocrHint')}</p>
                                <div className={styles.retakeAction}>
                                    <Button variant="secondary" size="lg" block onClick={handleRetake}>
                                        {t('costComp.retake')}
                                    </Button>
                                </div>
                            </Section>

                            <ButtonFooter>
                                <Button variant="primary" size="lg" block onClick={handleConfirm}>
                                    {t('common.confirm')}
                                </Button>
                            </ButtonFooter>
                        </>
                    )}
                </>
            )}
        </AppScreen>
    );
};
